"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.MessagePanelProvider = void 0;
const react_1 = __importStar(require("react"));
const MessagePanelActionsContext_1 = require("./MessagePanelActionsContext");
const MessagePanelContext_1 = require("./MessagePanelContext");
// Lets a single row subscribe to only its own selection toggles instead of
// every consumer re-rendering on any change to the shared selectedMessages
// array — a plain Context value can't offer that granularity on its own.
function createMessageSelectionStore() {
    const listenersByMessageId = new Map();
    const selectedIds = new Set();
    const notify = (messageId) => {
        var _a;
        (_a = listenersByMessageId.get(messageId)) === null || _a === void 0 ? void 0 : _a.forEach(listener => listener());
    };
    return {
        subscribe: (messageId, listener) => {
            let listeners = listenersByMessageId.get(messageId);
            if (!listeners) {
                listeners = new Set();
                listenersByMessageId.set(messageId, listeners);
            }
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
                if (listeners.size === 0) {
                    listenersByMessageId.delete(messageId);
                }
            };
        },
        isSelected: (messageId) => selectedIds.has(messageId),
        toggle: (messageId) => {
            const willBeSelected = !selectedIds.has(messageId);
            if (willBeSelected) {
                selectedIds.add(messageId);
            }
            else {
                selectedIds.delete(messageId);
            }
            notify(messageId);
            return willBeSelected;
        },
        clear: () => {
            const ids = Array.from(selectedIds);
            selectedIds.clear();
            ids.forEach(notify);
        },
    };
}
const MessagePanelProvider = ({ children, }) => {
    const [scrollToBottom, setScrollToBottom] = (0, react_1.useState)();
    const [focusedMessage, setFocusedMessage] = (0, react_1.useState)(undefined);
    const [selectedMessages, setCurrentMessages] = (0, react_1.useState)([]);
    const [messageToEdit, setMessageToEdit] = (0, react_1.useState)();
    // useRef's argument is evaluated on every render even though only the
    // first result is kept — lazy-init via getSelectionStore() so a throwaway
    // store isn't allocated (and immediately discarded) on every re-render.
    const selectionStoreRef = (0, react_1.useRef)(null);
    const getSelectionStore = () => {
        if (!selectionStoreRef.current) {
            selectionStoreRef.current = createMessageSelectionStore();
        }
        return selectionStoreRef.current;
    };
    const clearFocusedMessage = (0, react_1.useCallback)(() => {
        setFocusedMessage(undefined);
    }, []);
    const addToSelectedMessage = (0, react_1.useCallback)((newMessage) => {
        const messageId = newMessage.getMessage().id;
        const willBeSelected = getSelectionStore().toggle(messageId);
        setCurrentMessages((prevMessages) => willBeSelected
            ? [...prevMessages, newMessage]
            : prevMessages.filter(message => message.getMessage().id !== messageId));
    }, []);
    const clearSelectedMessages = (0, react_1.useCallback)(() => {
        getSelectionStore().clear();
        setCurrentMessages([]);
    }, []);
    const subscribeToMessageSelection = (0, react_1.useCallback)((messageId, listener) => getSelectionStore().subscribe(messageId, listener), []);
    const isMessageSelectedById = (0, react_1.useCallback)((messageId) => getSelectionStore().isSelected(messageId), []);
    const value = (0, react_1.useMemo)(() => ({
        scrollToBottom,
        setScrollToBottom,
        focusedMessage,
        setFocusedMessage,
        clearFocusedMessage,
        selectedMessages,
        addToSelectedMessage,
        clearSelectedMessages,
        messageToEdit,
        setMessageToEdit,
    }), [
        scrollToBottom,
        focusedMessage,
        selectedMessages,
        clearFocusedMessage,
        addToSelectedMessage,
        clearSelectedMessages,
        messageToEdit,
    ]);
    // Every field here is a stable reference (state setter / useCallback with
    // fixed deps), so this object's identity never changes across renders —
    // consumers that only need these (e.g. a message row's own selection
    // check) never re-render just because `value` above changed for some
    // other message.
    const actionsValue = (0, react_1.useMemo)(() => ({
        addToSelectedMessage,
        subscribeToMessageSelection,
        isMessageSelectedById,
    }), [addToSelectedMessage, subscribeToMessageSelection, isMessageSelectedById]);
    return (react_1.default.createElement(MessagePanelActionsContext_1.MessagePanelActionsContext.Provider, { value: actionsValue },
        react_1.default.createElement(MessagePanelContext_1.MessagePanelContext.Provider, { value: value }, children)));
};
exports.MessagePanelProvider = MessagePanelProvider;
